import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'

interface BreadcrumbItem {
  label: string
  href?: string
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[]
  showHome?: boolean
}

// Route name mappings for automatic breadcrumb generation
const routeNames: Record<string, string> = {
  '': 'Dashboard',
  'controls': 'Controls',
  'internal-controls': 'Internal Controls',
  'evidence': 'Evidence',
  'users': 'Users',
  'risks': 'Risks',
  'frameworks': 'Frameworks',
  'activity-log': 'Activity Log',
  'reports': 'Reports',
  'settings': 'Settings',
  'integrations': 'Integrations',
  'documents': 'Documents',
  'tprm': 'TPRM',
  'tasks': 'Tasks',
  'mappings': 'Mappings',
  'scanners': 'Scanners',
  'help': 'Help',
  'new': 'New',
}

/**
 * Breadcrumb navigation component
 *
 * Can be used in two ways:
 * 1. Auto-generate from current path: <Breadcrumb />
 * 2. Provide custom items: <Breadcrumb items={[{ label: 'Controls', href: '/controls' }, { label: 'AC-1' }]} />
 */
export default function Breadcrumb({ items, showHome = true }: BreadcrumbProps) {
  const location = useLocation()

  // Generate breadcrumb items from current path if not provided
  const breadcrumbItems: BreadcrumbItem[] = items || (() => {
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const generatedItems: BreadcrumbItem[] = []

    let currentPath = ''
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i]
      currentPath += `/${segment}`

      // Skip UUID-like segments but use them as IDs for labels
      const isUUID = /^[0-9a-f-]{36}$/i.test(segment)
      const isNumeric = /^\d+$/.test(segment)

      if (isUUID || isNumeric) {
        // For IDs, we'll use a generic label - the page should override with actual data
        generatedItems.push({
          label: 'Details',
          href: i < pathSegments.length - 1 ? currentPath : undefined,
        })
      } else {
        const label = routeNames[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
        generatedItems.push({
          label,
          href: i < pathSegments.length - 1 ? currentPath : undefined,
        })
      }
    }

    return generatedItems
  })()

  // Don't show breadcrumb on dashboard (home page)
  if (location.pathname === '/' && !items) {
    return null
  }

  return (
    <nav className="flex items-center text-sm mb-4" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-1">
        {showHome && (
          <>
            <li>
              <Link
                to="/"
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Home"
              >
                <Home className="h-4 w-4" />
              </Link>
            </li>
            {breadcrumbItems.length > 0 && (
              <li>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </li>
            )}
          </>
        )}

        {breadcrumbItems.map((item, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <ChevronRight className="h-4 w-4 text-gray-300 mr-1" />
            )}
            {item.href ? (
              <Link
                to={item.href}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-gray-900 font-medium">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}
